import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import { useContentPlanStore } from '../store/contentPlanStore';
import { useStore } from '../store';
import { useArtistClient } from '../contexts/ArtistClientContext';
import { supabase } from '../lib/supabase';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Loader, AlertCircle, Plus, Download, Film, X, Search } from 'lucide-react';
import { ContentPlanPostModal } from './ContentPlanPostModal';
import { motion, AnimatePresence } from 'framer-motion';

// Add custom styles for today's date
const customStyles = `
  .rbc-today {
    background-color: transparent !important;
    border: 2px solid white !important;
    border-radius: 4px;
  }
`;

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ─── Custom Month Grid with 9:16 day cells + thumbnail backgrounds ───
interface VideoDropPayload {
  source: 'library';
  submissionId: string;
  projectName: string;
  type: 'song-specific' | 'off-topic';
  videoUrl: string;
  thumbnailUrl?: string | null;
}

interface CustomMonthGridProps {
  currentDate: Date;
  events: any[];
  onSelectSlot: (slot: { start: Date; action: string }) => void;
  onSelectEvent: (event: any) => void;
  onEventDrop: (data: { event: any; start: Date }) => void;
  onVideoDrop?: (payload: VideoDropPayload, date: Date) => void;
  draggedPost: string | null;
  artistReleases: Array<{ id: string; name: string; release_date: string | null }>;
  artistId?: string;
  dbClient?: SupabaseClient;
  onVisibleMonthChange?: (date: Date) => void;
}

const CustomMonthGrid: React.FC<CustomMonthGridProps> = ({
  currentDate,
  events,
  onSelectSlot,
  onSelectEvent,
  onEventDrop,
  onVideoDrop,
  draggedPost,
  artistReleases,
  artistId,
  dbClient,
  onVisibleMonthChange,
}) => {
  const [dragOverDate, setDragOverDate] = useState<Date | null>(null);
  const [overrides, setOverrides] = useState<Map<string, 'song-specific' | 'off-topic'>>(new Map());
  const [visibleMonthOverride, setVisibleMonthOverride] = useState<Date | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const monthRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const dayCellRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const todayCellRef = useRef<HTMLDivElement | null>(null);
  const hasInitiallyScrolled = useRef(false);

  // Generate months: 2 months before to 12 months after today
  // Each month's grid starts from the day after the previous month's last day,
  // so no weeks are duplicated across month boundaries.
  const months = useMemo(() => {
    const today = new Date();
    const baseYear = today.getFullYear();
    const baseMonth = today.getMonth();
    const result: { year: number; month: number; allDays: Date[]; monthLabel: string }[] = [];
    let nextStart: Date | null = null; // tracks where the next month should begin
    for (let offset = -2; offset <= 12; offset++) {
      const m = baseMonth + offset;
      const year = baseYear + Math.floor(m / 12);
      const month = ((m % 12) + 12) % 12;
      const firstOfMonth = new Date(year, month, 1);
      const startDay = (firstOfMonth.getDay() + 6) % 7; // Mon=0 ... Sun=6

      // Grid start: either the previous month's last day + 1, or the normal grid start
      let gridStart: Date;
      if (nextStart) {
        gridStart = new Date(nextStart);
      } else {
        gridStart = new Date(year, month, 1 - startDay);
      }

      // Calculate how many days we need: enough to cover all days of this month
      // plus trailing days to complete the last week row
      const lastOfMonth = new Date(year, month + 1, 0);
      const daysFromGridStart = Math.floor((lastOfMonth.getTime() - gridStart.getTime()) / 86400000) + 1;
      // Round up to complete weeks
      const numDays = Math.ceil(daysFromGridStart / 7) * 7;

      const allDays: Date[] = [];
      for (let i = 0; i < numDays; i++) {
        const d = new Date(gridStart);
        d.setDate(gridStart.getDate() + i);
        allDays.push(d);
      }

      // Next month starts the day after this month's last grid day
      const lastGridDay = allDays[allDays.length - 1];
      nextStart = new Date(lastGridDay);
      nextStart.setDate(lastGridDay.getDate() + 1);

      result.push({
        year,
        month,
        allDays,
        monthLabel: new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      });
    }
    return result;
  }, []);

  const today = new Date();

  // Fetch schedule overrides for the full range
  useEffect(() => {
    if (!artistId) return;
    const db = (dbClient || supabase) as typeof supabase;
    const firstMonth = months[0];
    const lastMonth = months[months.length - 1];
    const gridStart = firstMonth.allDays[0];
    const gridEnd = lastMonth.allDays[lastMonth.allDays.length - 1];

    const fetchData = async () => {
      const { data, error } = await (db as any)
        .from('content_plan_schedule_overrides')
        .select('schedule_date, type')
        .eq('artist_id', artistId)
        .gte('schedule_date', gridStart.toISOString().split('T')[0])
        .lte('schedule_date', gridEnd.toISOString().split('T')[0]);
      if (error) {
        console.error('Failed to fetch schedule overrides:', error);
        return;
      }
      const map = new Map<string, 'song-specific' | 'off-topic'>();
      for (const row of (data || []) as any[]) {
        map.set(row.schedule_date, row.type);
      }
      setOverrides(map);
    };
    fetchData();
  }, [artistId, months, dbClient]);

  // Group events by date string (YYYY-MM-DD)
  const eventsByDate = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const ev of events) {
      const d = new Date(ev.start);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    }
    return map;
  }, [events]);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const handleDragStart = (e: React.DragEvent, event: any) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ eventId: event.id }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!dragOverDate || !isSameDay(dragOverDate, date)) setDragOverDate(date);
  };

  const handleDrop = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    setDragOverDate(null);
    try {
      const raw = e.dataTransfer.getData('text/plain');
      if (!raw) return;
      const payload = JSON.parse(raw);
      // Drop from the video library → create a new scheduled post
      if (payload && payload.source === 'library' && payload.submissionId) {
        onVideoDrop?.(payload as VideoDropPayload, date);
        return;
      }
      // Existing post move
      const { eventId } = payload;
      const event = events.find(ev => ev.id === eventId);
      if (event) {
        onEventDrop({ event, start: date });
      }
    } catch {}
  };

  // Scroll to today's month on mount, then to currentDate when it changes
  useEffect(() => {
    if (!gridRef.current) return;
    const grid = gridRef.current;

    const doScroll = (attempt: number) => {
      if (!hasInitiallyScrolled.current) {
        const today = new Date();
        const todayKey = `${today.getFullYear()}-${today.getMonth()}`;
        const todayMonthEl = monthRefs.current.get(todayKey);

        if (todayCellRef.current && grid) {
          // Scroll so exactly 1 week (1 row) is visible above today
          const cell = todayCellRef.current;
          const gridRect = grid.getBoundingClientRect();
          const cellRect = cell.getBoundingClientRect();

          // Find the cell 7 days before today to measure actual row height
          const today = new Date();
          const prevWeek = new Date(today);
          prevWeek.setDate(prevWeek.getDate() - 7);
          const prevKey = `${prevWeek.getFullYear()}-${prevWeek.getMonth()}-${prevWeek.getDate()}`;
          const prevCell = dayCellRefs.current.get(prevKey);

          let rowHeight: number;
          if (prevCell) {
            // Measure actual distance between today and 7 days ago
            const prevRect = prevCell.getBoundingClientRect();
            rowHeight = cellRect.top - prevRect.top;
          } else {
            // Fallback: estimate from cell height + computed gap
            const gridStyle = window.getComputedStyle(grid.querySelector('.grid') || grid);
            const gap = parseFloat(gridStyle.rowGap) || 8;
            rowHeight = cellRect.height + gap;
          }

          const offset = cellRect.top - gridRect.top + grid.scrollTop - rowHeight;
          grid.scrollTo({ top: Math.max(0, offset), behavior: 'auto' });
          onVisibleMonthChange?.(today);
          hasInitiallyScrolled.current = true;
        } else if (todayMonthEl) {
          // Fallback: scroll to today's month
          todayMonthEl.scrollIntoView({ block: 'start' });
          onVisibleMonthChange?.(today);
          hasInitiallyScrolled.current = true;
        } else if (attempt < 5) {
          // Retry — refs might not be set yet
          setTimeout(() => doScroll(attempt + 1), 100);
        }
      } else {
        // Navigation click: scroll to target month
        const targetKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;
        const targetEl = monthRefs.current.get(targetKey);
        if (targetEl) {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          onVisibleMonthChange?.(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
        }
      }
    };

    const timer = setTimeout(() => doScroll(0), 100);

    return () => clearTimeout(timer);
  }, [currentDate, months, onVisibleMonthChange]);

  // Track which month is visible during scroll
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const gridRect = grid.getBoundingClientRect();
        const midY = gridRect.top + gridRect.height / 2;
        let closest: { el: HTMLDivElement; dist: number; date: Date } | null = null;
        for (const [key, el] of monthRefs.current) {
          const r = el.getBoundingClientRect();
          if (r.bottom < 0 || r.top > gridRect.bottom) continue;
          const dist = Math.abs(r.top + r.height / 2 - midY);
          if (!closest || dist < closest.dist) {
            const [y, m] = key.split('-').map(Number);
            closest = { el, dist, date: new Date(y, m, 1) };
          }
        }
        if (closest) {
          setVisibleMonthOverride(closest.date);
          onVisibleMonthChange?.(closest.date);
        }
        ticking = false;
      });
    };
    grid.addEventListener('scroll', onScroll, { passive: true });
    return () => grid.removeEventListener('scroll', onScroll);
  }, [onVisibleMonthChange]);

  // Render a single day cell
  const renderDayCell = (date: Date, i: number, monthNum: number) => {
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    const dayEvents = eventsByDate.get(key) || [];
    const isToday = isSameDay(date, today);
    const isCurrentMonth = date.getMonth() === monthNum;
    const isDragOver = dragOverDate && isSameDay(dragOverDate, date);
    const thumbEvents = dayEvents.filter(e => e.thumbnailUrl);
    const thumbCount = thumbEvents.length;

    return (
      <div
        key={i}
        ref={(el) => {
          if (el) {
            dayCellRefs.current.set(key, el);
            if (isToday) todayCellRef.current = el;
          } else {
            dayCellRefs.current.delete(key);
          }
        }}
        onClick={() => onSelectSlot({ start: date, action: 'click' })}
        onDragOver={(e) => handleDragOver(e, date)}
        onDragLeave={() => setDragOverDate(null)}
        onDrop={(e) => handleDrop(e, date)}
        className={`relative rounded-lg overflow-hidden cursor-pointer transition-all ${
          isDragOver ? 'ring-2 ring-primary-500 scale-105' : ''
        } ${isCurrentMonth ? 'bg-gray-700/50' : 'bg-gray-800/30'}`}
        style={{ aspectRatio: '9 / 16' }}
      >
        {thumbCount > 0 ? (
          <>
            {/* Splitscreen thumbnails */}
            <div className="absolute inset-0 flex flex-col">
              {thumbEvents.slice(0, 4).map((ev: any) => (
                <div key={ev.id} className="flex-1 min-h-0 relative overflow-hidden">
                  <img
                    src={ev.thumbnailUrl}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />
          </>
        ) : (
          <div className="absolute inset-0" />
        )}

        {isToday && (
          <div className="absolute inset-0 border-2 border-white rounded-lg pointer-events-none z-30" />
        )}

        <div className="absolute top-1 left-1 z-20">
          <span className={`text-xs sm:text-sm font-semibold ${
            isToday
              ? 'bg-white text-gray-900 rounded-full w-6 h-6 flex items-center justify-center'
              : 'text-white'
          }`}>
            {date.getDate()}
          </span>
        </div>

        {/* Colored contour borders for posting start / release day */}
        {dayEvents.some(e => e.isPostingStart) && (
          <div
            className="absolute inset-1 rounded-md pointer-events-none z-10"
            style={{ border: '2px solid #fbbf24' }}
            title={dayEvents.find(e => e.isPostingStart)?.title}
          />
        )}
        {dayEvents.some(e => e.isReleaseDay) && (
          <div
            className="absolute inset-1 rounded-md pointer-events-none z-10"
            style={{ border: '2px solid #3b82f6' }}
            title={dayEvents.find(e => e.isReleaseDay)?.title}
          />
        )}

        {/* Regular posts as chips at bottom */}
        {(() => {
          const regularEvents = dayEvents.filter(e => !e.isPostingStart && !e.isReleaseDay);
          if (regularEvents.length === 0) return null;
          return (
            <div className="absolute bottom-0 left-0 right-0 z-20 p-1 space-y-0.5">
              {regularEvents.slice(0, 3).map((ev: any) => (
                <div
                  key={ev.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, ev)}
                  onClick={(e) => { e.stopPropagation(); onSelectEvent(ev); }}
                  className={`text-[10px] sm:text-xs truncate px-1 py-0.5 rounded cursor-move transition-all ${
                    draggedPost === ev.id ? 'opacity-50' : 'hover:scale-105'
                  } bg-gray-700/80 text-white`}
                  title={ev.title}
                >
                  {ev.title}
                </div>
              ))}
              {regularEvents.length > 3 && (
                <div className="text-[10px] text-white/70 px-1">+{regularEvents.length - 3} more</div>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Weekday header (fixed) */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 pb-1 flex-shrink-0">
        {WEEKDAYS.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-400 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Month label (fixed, updates on scroll) */}
      <div className="text-center text-sm font-semibold text-gray-300 py-2 flex-shrink-0 bg-black">
        {(() => {
          const today = new Date();
          const v = visibleMonthOverride || today;
          return v.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        })()}
      </div>

      {/* Scroll container — only day cells scroll */}
      <div ref={gridRef} className="flex-1 overflow-y-auto scrollbar-hide space-y-1 sm:space-y-2">
        {months.map((m) => {
          const key = `${m.year}-${m.month}`;
          return (
            <div
              key={key}
              ref={(el) => {
                if (el) monthRefs.current.set(key, el);
                else monthRefs.current.delete(key);
              }}
            >
              {/* Day cells for this month */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {m.allDays.map((date, i) => renderDayCell(date, i, m.month))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface ContentPlanCalendarProps {
  artistId?: string;
  embedded?: boolean;
  releaseDate?: string; // ISO date string
}

export const ContentPlanCalendar: React.FC<ContentPlanCalendarProps> = ({ artistId, embedded = false, releaseDate }) => {
  const {
    posts,
    loading,
    error,
    fetchPosts,
    setSelectedDate,
    movePost,
    addPost,
    getReadySubmissions
  } = useContentPlanStore();
  
  const { artists } = useStore();
  const { client: artistClient, isProtected, loading: clientLoading } = useArtistClient();
  const db = (isProtected ? artistClient : supabase) as typeof supabase;
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [draggedPost, setDraggedPost] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<'month' | 'agenda'>('month');
  const [downloadingVideos, setDownloadingVideos] = useState<Set<string>>(new Set());
  const [artistReleases, setArtistReleases] = useState<Array<{ id: string; name: string; release_date: string | null }>>([]);

  // Video library (ready submissions) for drag-and-drop onto the calendar
  const [readySubmissions, setReadySubmissions] = useState<any[]>([]);
  const [showVideoLibrary, setShowVideoLibrary] = useState(false);
  const [videoLibraryLoading, setVideoLibraryLoading] = useState(false);
  const [videoSearch, setVideoSearch] = useState('');
  const [droppingVideoId, setDroppingVideoId] = useState<string | null>(null);

  const fetchReadySubmissions = async () => {
    setVideoLibraryLoading(true);
    try {
      // When artistId is provided, fetch that artist's ready videos.
      // When omitted (admin "All Artists" view), fetch all ready videos.
      const submissions = await getReadySubmissions(artistId, isProtected ? artistClient : undefined);
      setReadySubmissions(submissions as any);
    } catch (err) {
      console.error('Error fetching ready submissions for library:', err);
      setReadySubmissions([]);
    } finally {
      setVideoLibraryLoading(false);
    }
  };

  // Fetch ready submissions whenever artist / auth state changes
  useEffect(() => {
    if (clientLoading) return;
    fetchReadySubmissions();
  }, [artistId, clientLoading, isProtected, artistClient, getReadySubmissions]);
  
  // State for drag and drop of posting start
  const [draggingPostingStart, setDraggingPostingStart] = useState<{ releaseId: string; releaseName: string; originalDate: string } | null>(null);
  const [showPostingStartConfirm, setShowPostingStartConfirm] = useState(false);
  const [pendingPostingStartMove, setPendingPostingStartMove] = useState<{ releaseId: string; newPostingStartDate: Date; newReleaseDate: Date } | null>(null);
  const [justDropped, setJustDropped] = useState(false);
  
  // Function to fetch releases - can be called manually to refresh
  const fetchReleases = async () => {
    if (!artistId) {
      setArtistReleases([]);
      return;
    }

    try {
      // Fetch from releases table
      const { data: releasesData, error: releasesError } = await db
        .from('releases')
        .select('id, name, release_date')
        .eq('artist_id', artistId as any)
        .not('release_date', 'is', null);

      if (releasesError) throw releasesError;

      // Fetch from content_plan_release_dates table
      const { data: customDatesData, error: customDatesError } = await db
        .from('content_plan_release_dates')
        .select('id, name, release_date')
        .eq('artist_id', artistId as any);
      
      if (customDatesError) {
        console.error('Error fetching custom release dates:', customDatesError);
      }
      
      // Combine both sources
      const allReleases = [
        ...(releasesData || []),
        ...(customDatesData || [])
      ];
      
      setArtistReleases(allReleases as any);
    } catch (err) {
      console.error('Error fetching releases for calendar:', err);
    }
  };
  
  // Fetch releases on mount and when artistId changes
  useEffect(() => {
    if (clientLoading) return;
    fetchReleases();
  }, [artistId, clientLoading, isProtected, artistClient]);
  
  // Initialize currentDate with releaseDate if provided, otherwise use today
  const [currentDate] = useState(() => {
    if (releaseDate) {
      const date = new Date(releaseDate);
      return isNaN(date.getTime()) ? new Date() : date;
    }
    return new Date();
  });

  // Track which month is visible in the scroll (for header display)
  const [visibleMonth, setVisibleMonth] = useState(() => {
    if (releaseDate) {
      const date = new Date(releaseDate);
      return isNaN(date.getTime()) ? new Date() : date;
    }
    return new Date();
  });
  
  useEffect(() => {
    if (clientLoading) return;
    fetchPosts(artistId, isProtected ? artistClient : undefined);
  }, [fetchPosts, artistId, clientLoading, isProtected, artistClient]);
  
  // Filter posts by artistId if provided
  const filteredPosts = useMemo(() => {
    if (artistId) {
      return posts.filter(post => post.resourceId === artistId);
    }
    return posts;
  }, [posts, artistId]);
  
  // Create posting start and release day events for calendar display
  const releaseEvents = useMemo(() => {
    const events: any[] = [];
    const seenPostingStarts = new Set<string>(); // Track posting start dates to prevent duplicates
    const seenReleaseDays = new Set<string>(); // Track release dates to prevent duplicates
    
    artistReleases.forEach(release => {
      if (!release.release_date) return;
      const relDate = new Date(release.release_date);
      
      // Posting Start event (11 days before release)
      const postingStartDate = new Date(relDate);
      postingStartDate.setDate(postingStartDate.getDate() - 11);
      const postingStartKey = postingStartDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      // Only add posting start event if we haven't seen this date yet
      if (!seenPostingStarts.has(postingStartKey)) {
        seenPostingStarts.add(postingStartKey);
        events.push({
          id: `posting-start-${release.id}`,
          title: '📢 Posting Start',
          start: postingStartDate,
          end: postingStartDate,
          allDay: true,
          isPostingStart: true,
          releaseId: release.id,
          releaseName: release.name,
          releaseDate: release.release_date
        });
      }
      
      // Release Day event
      const releaseDayKey = relDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      // Only add release day event if we haven't seen this date yet
      if (!seenReleaseDays.has(releaseDayKey)) {
        seenReleaseDays.add(releaseDayKey);
        events.push({
          id: `release-day-${release.id}`,
          title: '🎵 Release Day',
          start: relDate,
          end: relDate,
          allDay: true,
          isReleaseDay: true,
          releaseId: release.id,
          releaseName: release.name,
          releaseDate: release.release_date
        });
      }
    });
    
    return events;
  }, [artistReleases]);
  
  // Combined events for calendar (posts + release events)
  const calendarEvents = useMemo(() => {
    return [...filteredPosts, ...releaseEvents];
  }, [filteredPosts, releaseEvents]);
  
  const handleSelectSlot = ({ start, action }: { start: Date; action: string }) => {
    // Don't open modal if we're dragging posting start or just dropped
    if (draggingPostingStart || justDropped) {
      return;
    }
    // Only show add modal when clicking, not when dragging
    if (action === 'click' || action === 'select') {
      setSelectedDate(start);
      setSelectedPost(null);
      setShowPostModal(true);
    }
  };
  
  const handleSelectPost = (post: any) => {
    // Don't open modal for posting start or release day events when clicking
    if (post.isPostingStart || post.isReleaseDay) {
      return;
    }
    setSelectedPost(post);
    setShowPostModal(true);
  };

  const handleEventDrop = async ({ event, start }: any) => {
    // Handle posting start event drop - adjusts release date forward
    if (event.isPostingStart) {
      // Set justDropped to prevent modal from opening
      setJustDropped(true);
      setTimeout(() => setJustDropped(false), 500);
      
      // Calculate new release date (posting start + 11 days)
      const newPostingStartDate = new Date(start);
      const newReleaseDate = new Date(newPostingStartDate);
      newReleaseDate.setDate(newReleaseDate.getDate() + 11);
      
      // Set pending move and show confirmation
      setPendingPostingStartMove({
        releaseId: event.releaseId,
        newPostingStartDate,
        newReleaseDate
      });
      setShowPostingStartConfirm(true);
      return;
    }
    
    // Handle release day event drop - adjusts posting start backward
    if (event.isReleaseDay) {
      // Set justDropped to prevent modal from opening
      setJustDropped(true);
      setTimeout(() => setJustDropped(false), 500);
      
      // Calculate new posting start date (release date - 11 days)
      const newReleaseDate = new Date(start);
      const newPostingStartDate = new Date(newReleaseDate);
      newPostingStartDate.setDate(newPostingStartDate.getDate() - 11);
      
      // Set pending move and show confirmation
      setPendingPostingStartMove({
        releaseId: event.releaseId,
        newPostingStartDate,
        newReleaseDate
      });
      setShowPostingStartConfirm(true);
      return;
    }
    
    // Set dragged state for visual feedback
    setDraggedPost(event.id);
    
    // Move the post (this now updates UI optimistically)
    await movePost(event.id, start, isProtected ? artistClient : undefined);
    
    // Clear dragged state
    setDraggedPost(null);
  };

  // Handle dropping a video from the library onto a calendar day → schedule it
  const handleVideoDrop = async (payload: any, date: Date) => {
    // Use the passed artistId, or fall back to the submission's own artistId
    // (admin "All Artists" view — each video carries its own artistId)
    const targetArtistId = artistId || payload.artistId;
    if (!targetArtistId) return;

    // Prevent the click-to-open-modal that follows a drop
    setJustDropped(true);
    setTimeout(() => setJustDropped(false), 500);

    const endDate = new Date(date);
    endDate.setHours(23, 59, 59);

    try {
      await addPost({
        title: payload.projectName,
        start: date,
        end: endDate,
        allDay: true,
        resourceId: targetArtistId,
        submissionId: payload.submissionId,
        status: 'posted',
        type: payload.type,
        videoUrl: payload.videoUrl,
        thumbnailUrl: payload.thumbnailUrl ?? null,
        notes: null,
      } as any, isProtected ? artistClient : undefined);

      // Remove the dropped video from the library list (its status is no longer 'ready')
      setReadySubmissions(prev => prev.filter(s => String(s.id) !== String(payload.submissionId)));
    } catch (err) {
      console.error('Error scheduling video via drag-and-drop:', err);
    }
  };

  // Helper function to handle video download
  const handleDownload = (videoUrl: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    // Add video to downloading state
    setDownloadingVideos(prev => new Set(prev).add(videoUrl));
    
    try {
      // Extract filename from URL for better download naming
      let filename = 'video.mp4';
      try {
        const urlPath = new URL(videoUrl).pathname;
        const pathParts = urlPath.split('/');
        const lastPart = pathParts[pathParts.length - 1];
        if (lastPart && lastPart.includes('.')) {
          filename = lastPart;
        }
      } catch {
        // Use default filename if URL parsing fails
      }
      
      // For Dropbox URLs, modify to use dl=1 for direct download
      if (videoUrl.includes('dropbox.com')) {
        try {
          const urlObj = new URL(videoUrl);
          urlObj.searchParams.set('dl', '1');
          const downloadUrl = urlObj.toString();
          
          // Create a temporary link element for download
          const a = document.createElement('a');
          a.href = downloadUrl;
          a.download = filename;
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          
          // Remove from downloading state after a short delay
          setTimeout(() => {
            setDownloadingVideos(prev => {
              const newSet = new Set(prev);
              newSet.delete(videoUrl);
              return newSet;
            });
          }, 1000);
        } catch (error) {
          console.error('Error processing Dropbox URL:', error);
          // Remove from downloading state
          setDownloadingVideos(prev => {
            const newSet = new Set(prev);
            newSet.delete(videoUrl);
            return newSet;
          });
          // Fallback to opening the original URL
          window.open(videoUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        // For non-Dropbox URLs (Supabase storage), use Edge Function
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const downloadUrl = `${supabaseUrl}/functions/v1/force-download?url=${encodeURIComponent(videoUrl)}&filename=${encodeURIComponent(filename)}`;
        
        // Fetch with authorization header to trigger download
        fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
        })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          // Create a download link
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          
          // Remove from downloading state
          setDownloadingVideos(prev => {
            const newSet = new Set(prev);
            newSet.delete(videoUrl);
            return newSet;
          });
        })
        .catch(error => {
          console.error('Error downloading via Edge Function:', error);
          
          // Remove from downloading state
          setDownloadingVideos(prev => {
            const newSet = new Set(prev);
            newSet.delete(videoUrl);
            return newSet;
          });
          
          // Fallback to direct URL
          window.open(videoUrl, '_blank', 'noopener,noreferrer');
        });
      }
    } catch (error) {
      console.error('Error downloading video:', error);
      // Remove from downloading state on error
      setDownloadingVideos(prev => {
        const newSet = new Set(prev);
        newSet.delete(videoUrl);
        return newSet;
      });
    }
  };



  
  if (loading && filteredPosts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full min-h-0">
      <style>{customStyles}</style>
      {!embedded && (
        <AnimatePresence>
          {error && (
            <motion.div 
              className="flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-md mb-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p>{error}</p>
              <motion.button
                onClick={() => fetchPosts(artistId, isProtected ? artistClient : undefined)}
                className="ml-auto text-sm text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Try Again
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      )}
      
      {!embedded && (
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-semibold dark:text-white">
            {artistId ? `${artists.find(a => a.id === artistId)?.name}'s Content Plan` : 'Content Plan'}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setSelectedDate(new Date());
                setSelectedPost(null);
                setShowPostModal(true);
              }}
              className="btn flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              <span>Add Post</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Navigation Header - Always Visible */}
      <div className={`flex justify-between items-center ${embedded ? 'mb-2' : 'mb-4'}`}>
        <div className="text-center">
          <span className="text-lg font-medium dark:text-white">
            {moment(visibleMonth).format('MMMM YYYY')}
          </span>
        </div>
        
        <div className="flex space-x-2 items-center">
          <motion.button
            type="button"
            className={`px-4 py-2 text-sm font-medium rounded-md flex items-center gap-1.5 ${
              showVideoLibrary
                ? 'bg-primary-500 text-white'
                : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            onClick={() => {
              setShowVideoLibrary(v => !v);
              if (!showVideoLibrary) fetchReadySubmissions();
            }}
            whileTap={{ scale: 0.95 }}
            title="Toggle video library — drag videos onto the calendar to schedule them"
          >
            <Film className="h-4 w-4" />
            <span>Videos</span>
            {readySubmissions.length > 0 && (
              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                showVideoLibrary ? 'bg-white/20' : 'bg-primary-500 text-white'
              }`}>
                {readySubmissions.length}
              </span>
            )}
          </motion.button>
          <motion.button
            type="button"
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              currentView === 'agenda'
                ? 'bg-primary-500 text-white'
                : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
            onClick={() => setCurrentView(currentView === 'agenda' ? 'month' : 'agenda')}
            whileTap={{ scale: 0.95 }}
          >
            <Download className="h-4 w-4 inline mr-1" />
            Download
          </motion.button>
        </div>
      </div>
      
      <div className="flex-1 min-h-0 flex gap-2 overflow-hidden">
        {/* Video library panel — draggable ready videos */}
        <AnimatePresence>
          {showVideoLibrary && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 280, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex-shrink-0 overflow-hidden"
            >
              <div className="h-full w-[280px] bg-gray-900 rounded-lg shadow-md flex flex-col overflow-hidden border border-gray-700">
                <div className="flex items-center justify-between p-3 border-b border-gray-700 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <Film className="h-4 w-4 text-primary-500" />
                    <h3 className="text-sm font-semibold text-white">Ready Videos</h3>
                  </div>
                  <button
                    onClick={() => setShowVideoLibrary(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                    title="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="p-2 border-b border-gray-700 flex-shrink-0">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                    <input
                      type="text"
                      value={videoSearch}
                      onChange={(e) => setVideoSearch(e.target.value)}
                      placeholder="Search videos..."
                      className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
                  {videoLibraryLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader className="h-5 w-5 animate-spin text-primary-500" />
                    </div>
                  ) : readySubmissions.length === 0 ? (
                    <div className="text-center py-8 px-3">
                      <p className="text-sm text-gray-400">
                        No ready videos available.
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Drag a video from here onto a calendar day to schedule it.
                      </p>
                    </div>
                  ) : (
                    (() => {
                      const filtered = videoSearch.trim() === ''
                        ? readySubmissions
                        : readySubmissions.filter((s: any) =>
                            (s.projectName || '').toLowerCase().includes(videoSearch.toLowerCase()) ||
                            (s.type || '').toLowerCase().includes(videoSearch.toLowerCase())
                          );
                      if (filtered.length === 0) {
                        return (
                          <div className="text-center py-6 text-sm text-gray-400">
                            No videos match your search.
                          </div>
                        );
                      }
                      return filtered.map((submission: any) => {
                        const subId = String(submission.id);
                        const artistName = artists.find(a => String(a.id) === String(submission.artistId))?.name;
                        return (
                          <div
                            key={subId}
                            draggable
                            onDragStart={(e) => {
                              setDroppingVideoId(subId);
                              e.dataTransfer.setData('text/plain', JSON.stringify({
                                source: 'library',
                                submissionId: subId,
                                projectName: submission.projectName,
                                type: submission.type,
                                videoUrl: submission.videoUrl,
                                thumbnailUrl: submission.thumbnailUrl ?? null,
                                artistId: submission.artistId,
                              }));
                              e.dataTransfer.effectAllowed = 'copy';
                            }}
                            onDragEnd={() => setDroppingVideoId(null)}
                            className={`group flex gap-2 p-2 rounded-md bg-gray-800 hover:bg-gray-700 cursor-grab active:cursor-grabbing transition-all border border-transparent hover:border-primary-500 ${
                              droppingVideoId === subId ? 'opacity-50' : ''
                            }`}
                            title={`${submission.projectName} — drag onto a calendar day to schedule`}
                          >
                            {/* Thumbnail */}
                            <div className="relative w-12 h-[68px] flex-shrink-0 rounded overflow-hidden bg-black">
                              {submission.thumbnailUrl ? (
                                <img
                                  src={submission.thumbnailUrl}
                                  alt=""
                                  className="absolute inset-0 w-full h-full object-cover"
                                  loading="lazy"
                                  draggable={false}
                                />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Film className="h-4 w-4 text-gray-600" />
                                </div>
                              )}
                            </div>
                            {/* Info */}
                            <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                              <div>
                                <p className="text-xs font-medium text-white truncate">
                                  {submission.projectName}
                                </p>
                                <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                                  submission.type === 'song-specific'
                                    ? 'bg-emerald-900/40 text-emerald-300'
                                    : 'bg-violet-900/40 text-violet-300'
                                }`}>
                                  {submission.type === 'song-specific' ? 'Song' : 'Off-Topic'}
                                </span>
                              </div>
                              <div>
                                {artistName && (
                                  <p className="text-[10px] text-primary-400 truncate">
                                    {artistName}
                                  </p>
                                )}
                                {submission.releases?.name && (
                                  <p className="text-[10px] text-gray-500 truncate">
                                    {submission.releases.name}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div
          className="bg-black rounded-lg shadow-md flex-1 p-2 min-h-0 flex flex-col overflow-hidden"
        >
        {currentView === 'month' ? (
          <CustomMonthGrid
            currentDate={currentDate}
            events={calendarEvents}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectPost}
            onEventDrop={handleEventDrop}
            onVideoDrop={handleVideoDrop}
            draggedPost={draggedPost}
            artistReleases={artistReleases}
            artistId={artistId}
            dbClient={isProtected ? artistClient : undefined}
            onVisibleMonthChange={setVisibleMonth}
          />
        ) : (
          /* Custom agenda table matching video submissions style with release grouping */
          (() => {
            // Check if any post has a release assigned
            const hasAnyRelease = filteredPosts.some(post => post.release_id);
            
            // Group posts by release, then sort by date within each group
            const releaseGroups = filteredPosts.reduce((groups, post) => {
              const releaseId = post.release_id || 'no-release';
              const releaseName = post.releases?.name || 'No Release Assigned';
              const releaseDate = post.releases?.release_date || null;
              
              if (!groups[releaseId]) {
                groups[releaseId] = {
                  releaseId,
                  releaseName,
                  releaseDate,
                  posts: []
                };
              }
              groups[releaseId].posts.push(post);
              return groups;
            }, {} as Record<string, { releaseId: string; releaseName: string; releaseDate: string | null; posts: typeof filteredPosts }>);
            
            // Sort release groups: newest release date first, then "No Release Assigned" at end
            const sortedReleaseGroups = Object.values(releaseGroups).sort((a, b) => {
              if (a.releaseId === 'no-release') return 1;
              if (b.releaseId === 'no-release') return -1;
              // Sort by release date descending (newest first)
              if (a.releaseDate && b.releaseDate) {
                return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
              }
              if (a.releaseDate) return -1;
              if (b.releaseDate) return 1;
              return a.releaseName.localeCompare(b.releaseName);
            });
            
            // Sort posts within each group by scheduled date
            sortedReleaseGroups.forEach(group => {
              group.posts.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
            });
            
            return (
              <div className="h-full overflow-hidden">
                <div className="overflow-x-auto h-full">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-black divide-y divide-gray-800">
                      {filteredPosts.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                            No scheduled posts found
                          </td>
                        </tr>
                      ) : hasAnyRelease ? (
                        /* Release-grouped view */
                        sortedReleaseGroups.map((group) => (
                          <React.Fragment key={group.releaseId}>
                            {/* Release Header */}
                            <tr>
                              <td colSpan={4} className="px-6 py-2" style={{ backgroundColor: '#0000fe' }}>
                                <div className="text-sm font-medium text-white">
                                  {group.releaseName} ({group.posts.length})
                                </div>
                              </td>
                            </tr>
                            {/* Posts in this release group */}
                            {group.posts.map((post) => (
                              <tr 
                                key={post.id} 
                                className="hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                <td 
                                  className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 cursor-pointer"
                                  onClick={() => handleSelectPost(post)}
                                >
                                  {moment(post.start).format('DD.MM.YYYY')}
                                </td>
                                <td 
                                  className="px-6 py-4 whitespace-nowrap cursor-pointer"
                                  onClick={() => handleSelectPost(post)}
                                >
                                  <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700/20 dark:text-gray-200">
                                    {post.type === 'song-specific' ? 'Song Specific' : 'Off Topic'}
                                  </span>
                                </td>
                                <td 
                                  className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 cursor-pointer"
                                  onClick={() => handleSelectPost(post)}
                                >
                                  <div>
                                    <div className="font-medium">{post.title}</div>
                                    {post.notes && (
                                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap">
                                        {post.notes}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <motion.button
                                    onClick={(e) => handleDownload(post.videoUrl || '', e)}
                                    disabled={downloadingVideos.has(post.videoUrl || '')}
                                    className="text-white hover:text-gray-200 transition-colors"
                                    title="Download Video"
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                  >
                                    {downloadingVideos.has(post.videoUrl || '') ? (
                                      <Loader className="h-5 w-5 animate-spin" />
                                    ) : (
                                      <Download className="h-5 w-5" />
                                    )}
                                  </motion.button>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))
                      ) : (
                        /* Non-grouped view (no releases) */
                        filteredPosts
                          .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
                          .map((post) => (
                            <tr 
                              key={post.id} 
                              className="hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <td 
                                className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 cursor-pointer"
                                onClick={() => handleSelectPost(post)}
                              >
                                {moment(post.start).format('DD.MM.YYYY')}
                              </td>
                              <td 
                                className="px-6 py-4 whitespace-nowrap cursor-pointer"
                                onClick={() => handleSelectPost(post)}
                              >
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  post.type === 'song-specific'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200'
                                    : 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-200'
                                }`}>
                                  {post.type === 'song-specific' ? 'Song Specific' : 'Off Topic'}
                                </span>
                              </td>
                              <td 
                                className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 cursor-pointer"
                                onClick={() => handleSelectPost(post)}
                              >
                                <div>
                                  <div className="font-medium">{post.title}</div>
                                  {post.notes && (
                                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap">
                                      {post.notes}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <motion.button
                                  onClick={(e) => handleDownload(post.videoUrl || '', e)}
                                  disabled={downloadingVideos.has(post.videoUrl || '')}
                                  className="text-white hover:text-gray-200 transition-colors"
                                  title="Download Video"
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  {downloadingVideos.has(post.videoUrl || '') ? (
                                    <Loader className="h-5 w-5 animate-spin" />
                                  ) : (
                                    <Download className="h-5 w-5" />
                                  )}
                                </motion.button>
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()
        )}
      </div>
      </div>

      <AnimatePresence>
        {showPostModal && (
          <ContentPlanPostModal
            post={selectedPost}
            artistId={artistId}
            onClose={() => {
              setShowPostModal(false);
              // Refresh releases to pick up any new release dates
              fetchReleases();
              // Refresh the video library so newly scheduled/unscheduled videos update
              fetchReadySubmissions();
            }}
          />
        )}
      </AnimatePresence>
      
      {/* Confirmation modal for moving release schedule */}
      <AnimatePresence>
        {showPostingStartConfirm && pendingPostingStartMove && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div 
              className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <h3 className="text-lg font-medium mb-4 dark:text-white">Move Release Schedule?</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                <strong>New Release Date:</strong> {pendingPostingStartMove.newReleaseDate.toLocaleDateString('de-DE')}
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                <strong>New Posting Start:</strong> {pendingPostingStartMove.newPostingStartDate.toLocaleDateString('de-DE')}
              </p>
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-6">
                All posting schedule tags (Song/Off-Topic) will be recalculated accordingly.
              </p>
              <div className="flex justify-end space-x-2">
                <motion.button
                  onClick={() => {
                    setShowPostingStartConfirm(false);
                    setPendingPostingStartMove(null);
                    setDraggingPostingStart(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  onClick={async () => {
                    if (!pendingPostingStartMove || !artistId) return;
                    
                    try {
                      // Update the release date in content_plan_release_dates
                      const newReleaseDateStr = pendingPostingStartMove.newReleaseDate.toISOString().split('T')[0];
                      
                      const { error } = await db
                        .from('content_plan_release_dates')
                        .update({ release_date: newReleaseDateStr } as any)
                        .eq('id', pendingPostingStartMove.releaseId as any);

                      if (error) {
                        // Try updating in releases table instead
                        await db
                          .from('releases')
                          .update({ release_date: newReleaseDateStr } as any)
                          .eq('id', pendingPostingStartMove.releaseId as any);
                      }
                      
                      // Refresh releases
                      await fetchReleases();
                    } catch (err) {
                      console.error('Error updating release date:', err);
                    }
                    
                    setShowPostingStartConfirm(false);
                    setPendingPostingStartMove(null);
                    setDraggingPostingStart(null);
                  }}
                  className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Move Schedule
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};